import type { ReactNode } from 'react'
import { Button } from '@app/components/ui/button'
import { cn } from '@app/lib/cn'
import { formatNumber } from './format'

/**
 * Small shared building blocks for the production screens.
 *
 * The shared `components/ui/*` files aren't touched here; everything in this
 * file is only used within the production module.
 */

/* ── State screens ─────────────────────────────────────────────────────────── */

export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('block animate-pulse rounded-xs bg-hairline/70', className)}
    />
  )
}

/**
 * Failed load. `action` replaces the retry button for errors retrying cannot
 * fix — a 404 is the server's final answer, so the emir screen hands in a
 * "back to the list" link instead of a button that would fail again.
 */
export function ErrorState({
  title = 'Veri alınamadı',
  message,
  onRetry,
  action,
}: {
  title?: string
  message?: string
  onRetry?: () => void
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-start gap-2 px-3.5 py-6">
      <p className="font-display text-[0.9375rem] font-semibold text-ink">{title}</p>
      <p className="text-data text-ink-muted">
        {message ?? 'Bağlantı kurulamadı. Birkaç saniye sonra yeniden deneyin.'}
      </p>
      {action ? (
        <div className="mt-1">{action}</div>
      ) : onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-1">
          Yeniden dene
        </Button>
      ) : null}
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-start gap-2 px-3.5 py-8">
      <p className="font-display text-[0.9375rem] font-semibold text-ink">{title}</p>
      <p className="max-w-prose text-data text-ink-muted">{description}</p>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  )
}

/* ── Progress bar ──────────────────────────────────────────────────────────── */

export interface ProgressProps {
  total: number
  done: number
  inProgress: number
  waiting: number
  notStarted: number
  className?: string
}

/**
 * The emir's four states in a single bar: done (olive) · in progress (steel) ·
 * waiting (mustard) · not yet released to production (blank paper).
 */
export function Progress({
  total,
  done,
  inProgress,
  waiting,
  notStarted,
  className,
}: ProgressProps) {
  const denominator = total > 0 ? total : 1
  const slices = [
    { key: 'done', value: done, color: 'bg-zeytin', label: 'biten' },
    { key: 'inProgress', value: inProgress, color: 'bg-celik', label: 'işlemde' },
    { key: 'waiting', value: waiting, color: 'bg-hardal', label: 'bekleyen' },
    // One vocabulary for one state: the row text and the detail already say
    // "üretime verilmedi"; this tooltip said "başlamadı" for the same figure.
    { key: 'notStarted', value: notStarted, color: 'bg-kosele/12', label: 'üretime verilmedi' },
  ].filter((slice) => slice.value > 0)

  const title = slices.map((s) => `${formatNumber(s.value)} ${s.label}`).join(' · ')

  return (
    <span
      title={title || 'Hareket yok'}
      className={cn('flex h-1.5 w-full overflow-hidden rounded-xs bg-kagit', className)}
    >
      {slices.map((slice) => (
        <span
          key={slice.key}
          className={slice.color}
          style={{ width: `${(slice.value / denominator) * 100}%` }}
        />
      ))}
      <span className="sr-only">{title}</span>
    </span>
  )
}

/**
 * The bar's color key, drawn once per screen. The per-row `title` tooltip is
 * the only other decoding of the four slices, and it never shows on the shop
 * floor's touchscreens — without this the bar is unreadable exactly where the
 * screen is used most.
 */
export function ProgressLegend({ className }: { className?: string }) {
  const items = [
    { color: 'bg-zeytin', label: 'Biten' },
    { color: 'bg-celik', label: 'İşlemde' },
    { color: 'bg-hardal', label: 'Bekleyen' },
    { color: 'bg-kosele/12', label: 'Üretime verilmedi' },
  ]
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-x-3 gap-y-1', className)}>
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1 text-micro text-ink-muted">
          <span aria-hidden className={cn('size-2 shrink-0 rounded-xs', item.color)} />
          {item.label}
        </span>
      ))}
    </span>
  )
}

/* ── Number block ──────────────────────────────────────────────────────────── */

export function Stat({
  label,
  value,
  unit,
  tone = 'neutral',
  className,
}: {
  label: string
  value: string
  unit?: string
  tone?: 'neutral' | 'accent' | 'warn' | 'info' | 'ok'
  className?: string
}) {
  const color = {
    neutral: 'text-ink',
    accent: 'text-kordovan',
    warn: 'text-hardal',
    info: 'text-celik',
    ok: 'text-zeytin',
  }[tone]

  return (
    <div className={cn('min-w-0', className)}>
      <p className="eyebrow truncate">{label}</p>
      <p className={cn('font-mono text-lg leading-6 font-medium tnum', color)}>
        {value}
        {unit ? <span className="ml-1 text-micro text-ink-muted">{unit}</span> : null}
      </p>
    </div>
  )
}
